import React, { useState, useEffect, useRef, ReactNode } from "react";

export type InputTypes = React.HTMLInputTypeAttribute;
export type InputProps = React.InputHTMLAttributes<HTMLInputElement> & {
    className?: string;
    onChange?: (e: React.ChangeEvent<HTMLInputElement>) => void;
    onBlur?: (e: React.FocusEvent<HTMLInputElement>) => void;
    onFocus?: (e: React.FocusEvent<HTMLInputElement>) => void;
    label?: string;
    type?: InputTypes;
    disabled?: boolean;
    inputSize?: "sm" | "md" | "lg"; // 'md' is default
    error?: boolean | string;
    icon?: ReactNode;
    testId?: string;
};

const Input = (props: InputProps) => {
    const { inputSize, onChange, onBlur, value = ``, icon, label, ...rest } = props;
    const getInitial = () => String(value);
    const [cursor, setCursor] = useState<number | null>(null);
    const [initialValue] = useState<string>(getInitial());
    const [val, setVal] = useState<string>(getInitial());
    const ref = useRef<HTMLInputElement>(null);

    useEffect(() => {
        const input = ref.current;
        const allowedTypes = [`text`, `search`, `password`, `tel`, `url`];
        if (input && allowedTypes.includes(input.type) && value !== initialValue) {
            input.setSelectionRange(cursor, cursor);
        }
    }, [ref, cursor, value, initialValue]);

    const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
        if (e.target.selectionStart) {
            setCursor(e.target.selectionStart);
        }

        setVal(e.target.value.replace(/(\r\n|\n|\r)/gm, ``));
        if (onChange) {
            onChange(e);
        }
    };

    const handleBlur = (e: React.FocusEvent<HTMLInputElement, Element>) => {
        if (onBlur) {
            onBlur(e);
        }
    };

    const hasicon = !!icon;
    const inputClass = hasicon ? `pl-9` : ``;
    const labelClass = hasicon ? `left-9` : `left-3`;

    return (
        <div className="Input-wrapper relative w-full">
            {hasicon && <div className="Input-icon absolute top-[50%] left-3 -translate-y-1/2">{icon}</div>}

            <input
                ref={ref}
                id={props.name}
                value={val}
                onChange={handleChange}
                onBlur={handleBlur}
                {...rest}
                className={`Input z-10 h-10 w-full rounded-lg border  border-border-light px-3 text-base text-text-primary shadow-sm ${inputClass} placeholder-shown:bg-white placeholder:text-white`}
            />
            {label && (
                <label
                    htmlFor={props.name}
                    className={`Input-label pointer-events-none absolute top-3 -mt-1 origin-center cursor-text select-none text-body2 font-bold text-text-disabled transition-all duration-100 ease-in-out ${labelClass} ${
                        props.value && `pointer-events-none opacity-0`
                    }`}
                >
                    {label}
                </label>
            )}
            {!!rest.error && (
                <>
                    {rest.error !== `hidden` && <p className={`pl-0.5 absolute left-1 -bottom-5 w-full text-xs text-accent-primary`}>{rest.error}</p>}
                    <div className="absolute right-4 top-3 h-4 w-4 origin-center animate-ping rounded-full  bg-accent-primary" />
                </>
            )}
        </div>
    );
};

export default Input;
